import {
  Box,
  Button,
  CloseButton,
  Flex,
  Modal,
  ModalBody,
  ModalContent,
  ModalFooter,
  ModalHeader,
  ModalOverlay,
  Text,
  VStack,
} from "@chakra-ui/react";
import { DateTime } from "luxon";
import { FaClock, FaExternalLinkAlt, FaMapMarkerAlt, FaUser, FaUsers } from "react-icons/fa";
import { Lead } from "../../../models/Lead";
import { CalendarVisit } from "./VisitCalendar";
import { crm, CrmAvatar, Pill } from "./crmTheme";

const VisitDetailModal = ({
  visit,
  isOpen,
  onClose,
  onOpenLead,
}: {
  visit: CalendarVisit | null;
  isOpen: boolean;
  onClose: () => void;
  onOpenLead: (lead: Lead) => void;
}) => {
  if (!visit) return null;

  const { lead } = visit;
  const date = DateTime.fromISO(visit.scheduledDate);

  const addressParts = [
    lead.street
      ? `${lead.street}${lead.addressNumber ? `, ${lead.addressNumber}` : ""}`
      : null,
    lead.city,
    lead.state,
    lead.zipCode,
  ].filter(Boolean) as string[];

  return (
    <Modal isOpen={isOpen} onClose={onClose} isCentered size="md">
      <ModalOverlay bg="blackAlpha.600" backdropFilter="blur(2px)" />
      <ModalContent borderRadius="16px" boxShadow={crm.shadowLg}>
        <ModalHeader borderBottom={`1px solid ${crm.border}`}>
          <Flex justify="space-between" align="flex-start">
            <Box>
              <Text fontWeight="700" fontSize="md" color={crm.text}>
                {lead.name ?? "Lead sem nome"}
              </Text>
              {lead.tradeName && (
                <Text fontSize="sm" color={crm.textMuted} fontWeight="400">
                  {lead.tradeName}
                </Text>
              )}
            </Box>
            <CloseButton onClick={onClose} ml="8px" />
          </Flex>
        </ModalHeader>

        <ModalBody>
          <VStack align="stretch" spacing="12px">
            <Flex align="center" gap="10px">
              <Box color={crm.brand} fontSize="14px">
                <FaClock />
              </Box>
              <Text fontWeight="600" color={crm.text} textTransform="capitalize" fontSize="sm">
                {date.setLocale("pt-BR").toFormat("EEEE, dd/LL/yyyy")} às{" "}
                {date.toFormat("HH:mm")}
              </Text>
            </Flex>

            {visit.responsibleName && (
              <Flex align="center" gap="10px">
                <Box color={crm.brand} fontSize="14px">
                  <FaUser />
                </Box>
                <Flex align="center" gap="6px">
                  <CrmAvatar name={visit.responsibleName} size={22} />
                  <Text fontSize="sm" color={crm.text}>{visit.responsibleName}</Text>
                </Flex>
              </Flex>
            )}

            {visit.participantNames && visit.participantNames.length > 0 && (
              <Flex align="flex-start" gap="10px">
                <Box pt="2px" color={crm.brand} fontSize="14px">
                  <FaUsers />
                </Box>
                <Flex flexWrap="wrap" gap="4px">
                  {visit.participantNames.map((p, i) => (
                    <Pill key={i}>{p}</Pill>
                  ))}
                </Flex>
              </Flex>
            )}

            {addressParts.length > 0 && (
              <Flex align="center" gap="10px">
                <Box color={crm.brand} fontSize="14px">
                  <FaMapMarkerAlt />
                </Box>
                <Text color={crm.textMuted} fontSize="sm">
                  {addressParts.join(" — ")}
                </Text>
              </Flex>
            )}

            {visit.notes && (
              <Box
                p="12px"
                bg={crm.subtle}
                border={`1px solid ${crm.border}`}
                borderLeft={`3px solid ${crm.brand}`}
                borderRadius="8px"
              >
                <Text fontSize="xs" fontWeight="700" color={crm.textMuted} mb="4px">
                  Observações
                </Text>
                <Text fontSize="sm" color={crm.text}>
                  {visit.notes}
                </Text>
              </Box>
            )}
          </VStack>
        </ModalBody>

        <ModalFooter borderTop={`1px solid ${crm.border}`}>
          <Button
            leftIcon={<FaExternalLinkAlt />}
            variant="outline"
            borderColor={crm.brand}
            color={crm.brand}
            _hover={{ bg: crm.brandSoft }}
            borderRadius="10px"
            onClick={() => {
              onClose();
              onOpenLead(lead);
            }}
          >
            Abrir lead
          </Button>
        </ModalFooter>
      </ModalContent>
    </Modal>
  );
};

export default VisitDetailModal;
